// Toasts (ui-qol §10): a top-left stack at (10, 10), 300 px boxes, info blue / warning red at
// 80 %, 5 s each, 150 ms opacity fade, at most 4 (the oldest gives way). NOTICE messages,
// "Link copied!", controller plugged in, money notices, …
//
// `noticeBox` is the separate top-right white box agar uses for "Spectator seats are full."
// (ui-qol §8): 14 px #343434, a close ×, auto-hides after 5 s.
//
// Consumer: app.ts (NOTICE → toast text), ui/** screens.
//
// The same text shown again while its toast is still up restarts that toast's timer instead of
// stacking a duplicate.

import { h } from './dom.js';
import { t } from './i18n/t.js';

export const MAX_TOASTS = 4;
export const TOAST_MS = 5000;
const LEAVE_MS = 150;

export type ToastKind = 'info' | 'warning';

interface ToastEntry {
  readonly text: string;
  readonly el: HTMLElement;
  timer: ReturnType<typeof setTimeout> | null;
}

let root: HTMLElement | null = null;
const live: ToastEntry[] = [];
let box: { el: HTMLElement; timer: ReturnType<typeof setTimeout> | null } | null = null;

/** Sets the container (#toast-root) once at boot. */
export function initToasts(rootEl: HTMLElement): void {
  root = rootEl;
  root.classList.add('toast-root');
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');
}

function dismiss(entry: ToastEntry): void {
  const i = live.indexOf(entry);
  if (i < 0) return;
  live.splice(i, 1);
  if (entry.timer !== null) clearTimeout(entry.timer);
  entry.timer = null;
  entry.el.classList.add('is-leaving');
  setTimeout(() => entry.el.remove(), LEAVE_MS);
}

export function toast(text: string, durationMs = TOAST_MS, kind: ToastKind = 'info'): void {
  if (!root || text === '') return;
  const existing = live.find((e) => e.text === text);
  if (existing) {
    if (existing.timer !== null) clearTimeout(existing.timer);
    existing.timer = setTimeout(() => dismiss(existing), durationMs);
    return;
  }
  while (live.length >= MAX_TOASTS) {
    const oldest = live[0];
    if (!oldest) break;
    dismiss(oldest);
  }
  const el = h('div', { class: `toast toast-${kind}` }, text);
  const entry: ToastEntry = { text, el, timer: null };
  entry.timer = setTimeout(() => dismiss(entry), durationMs);
  live.push(entry);
  root.appendChild(el);
}

/** The top-right notice box (ui-qol §8, "Spectator seats are full."); replaces one that is up. */
export function noticeBox(text: string, durationMs = TOAST_MS): void {
  if (!root || text === '') return;
  closeNoticeBox();
  const close = (): void => closeNoticeBox();
  const el = h(
    'div',
    { class: 'notice-box', role: 'status' },
    h('span', { class: 'notice-box-text' }, text),
    h(
      'button',
      {
        type: 'button',
        class: 'notice-box-close',
        'aria-label': t('common.close'),
        onClick: close,
      },
      '×',
    ),
  );
  box = { el, timer: setTimeout(close, durationMs) };
  // Inside #toast-root (never made inert by a modal); `position: fixed` puts it top-right.
  root.appendChild(el);
}

export function closeNoticeBox(): void {
  if (!box) return;
  if (box.timer !== null) clearTimeout(box.timer);
  box.el.remove();
  box = null;
}

/** Removes every toast at once (app stop, tests). */
export function clearToasts(): void {
  for (const e of live.splice(0)) {
    if (e.timer !== null) clearTimeout(e.timer);
    e.el.remove();
  }
  closeNoticeBox();
}
